import React, { useState, useEffect } from 'react';
import {
  XCircle,
  Search,
  Download,
  RotateCcw,
  Archive,
  User,
  Building2,
  Mail,
  Phone,
  Calendar
} from 'lucide-react';
import type { Lead } from '../types';
import { leadsService } from '../services/supabaseService';
import ArchiveLeadModal from './ArchiveLeadModal';
import { useDataSubscription } from '../contexts/DataSyncContext';

const DiscardedLeadsList: React.FC = () => {
  const [discardedLeads, setDiscardedLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  useEffect(() => {
    loadDiscardedLeads();
  }, []);

  // Realtime: si otra pestaña o LeadsList restaura/archiva un lead descartado,
  // refrescamos la lista para no mostrar filas fantasma.
  useDataSubscription('leads', () => {
    loadDiscardedLeads();
  });

  const loadDiscardedLeads = async () => {
    try {
      setLoading(true);
      const data = await leadsService.getAllDiscarded();
      setDiscardedLeads(data);
      setFilteredLeads(data);
    } catch (error) {
      console.error('Error loading discarded leads:', error);
      alert('Error al cargar los leads descartados. Por favor, recarga la página.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);

    if (!term) {
      setFilteredLeads(discardedLeads);
      return;
    }

    const filtered = discardedLeads.filter(lead =>
      lead.fullName.toLowerCase().includes(term.toLowerCase()) ||
      lead.email.toLowerCase().includes(term.toLowerCase()) ||
      lead.company.toLowerCase().includes(term.toLowerCase()) ||
      lead.phone?.includes(term) ||
      lead.notes?.toLowerCase().includes(term.toLowerCase())
    );

    setFilteredLeads(filtered);
  };

  const handleRestore = async (lead: Lead, newStatus: Lead['status']) => {
    try {
      await leadsService.update(lead.id, { status: newStatus });
      await loadDiscardedLeads();
      setIsRestoreModalOpen(false);
      setSelectedLead(null);
      alert(`Lead restaurado exitosamente a estado: ${newStatus}`);
    } catch (error) {
      console.error('Error restoring lead:', error);
      alert('Error al restaurar el lead');
    }
  };

  const handleArchive = async (reason: string, note: string) => {
    if (!selectedLead) return;
    try {
      await leadsService.recordDisposition(selectedLead.id, 'archive', reason, note);
    } catch (error) {
      console.error('Error archiving lead:', error);
      alert('Error al archivar el lead');
      throw error;
    }
    void loadDiscardedLeads();
  };

  const exportToCSV = () => {
    const headers = ['Nombre', 'Email', 'Teléfono', 'Empresa', 'Notas', 'Fecha Descarte'];
    const csvContent = [
      headers.join(','),
      ...filteredLeads.map(lead => [
        lead.fullName,
        lead.email,
        lead.phone || '',
        lead.company,
        (lead.notes || '').replace(/,/g, ';'),
        lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString() : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads-descartados.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
        <span className="ml-3">Cargando leads descartados...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center space-x-3">
            <XCircle className="w-8 h-8 text-red-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Leads Descartados</h1>
              <p className="text-gray-600">Leads que han sido descartados del pipeline</p>
            </div>
          </div>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Exportar CSV</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Descartados</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{discardedLeads.length}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Elegibles para Archivo</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {discardedLeads.filter(l => l.eligibleForArchiveAt && new Date(l.eligibleForArchiveAt) <= new Date()).length}
              </p>
            </div>
            <Archive className="w-8 h-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, empresa o notas..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Discarded Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Descarte
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center">
                          <User className="h-5 w-5 text-red-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{lead.fullName}</div>
                        <div className="text-sm text-gray-500 flex items-center space-x-2">
                          <Mail className="w-3 h-3" />
                          <span>{lead.email}</span>
                        </div>
                        {lead.phone && (
                          <div className="text-sm text-gray-500 flex items-center space-x-2">
                            <Phone className="w-3 h-3" />
                            <span>{lead.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{lead.company}</div>
                        {lead.role && (
                          <div className="text-sm text-gray-500">{lead.role}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {lead.notes || 'Sin notas'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      {lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString() : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setIsRestoreModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded inline-flex items-center space-x-1"
                        title="Restaurar lead"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Restaurar</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setIsArchiveModalOpen(true);
                        }}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded inline-flex items-center space-x-1"
                        title="Archivar lead"
                      >
                        <Archive className="w-4 h-4" />
                        <span>Archivar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeads.length === 0 && (
          <div className="text-center py-12">
            <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay leads descartados</h3>
            <p className="text-gray-500">
              {searchTerm
                ? 'No se encontraron leads descartados con los criterios de búsqueda'
                : 'Los leads descartados aparecerán aquí'
              }
            </p>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mt-4 text-sm text-gray-500">
        Mostrando {filteredLeads.length} de {discardedLeads.length} leads descartados
      </div>

      {/* Restore Modal */}
      {isRestoreModalOpen && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <RotateCcw className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Restaurar Lead</h2>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  ¿A qué estado deseas restaurar este lead?
                </p>

                <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
                  <p className="text-sm"><span className="font-medium">Nombre:</span> {selectedLead.fullName}</p>
                  <p className="text-sm"><span className="font-medium">Empresa:</span> {selectedLead.company}</p>
                  <p className="text-sm"><span className="font-medium">Email:</span> {selectedLead.email}</p>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => handleRestore(selectedLead, 'nuevo')}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Restaurar como Nuevo
                  </button>
                  <button
                    onClick={() => handleRestore(selectedLead, 'contactado')}
                    className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    Restaurar como Contactado
                  </button>
                  <button
                    onClick={() => handleRestore(selectedLead, 'calificado')}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Restaurar como Calificado
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsRestoreModalOpen(false);
                  setSelectedLead(null);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      <ArchiveLeadModal
        isOpen={isArchiveModalOpen}
        onClose={() => {
          setIsArchiveModalOpen(false);
          setSelectedLead(null);
        }}
        action="archive"
        onConfirm={handleArchive}
        lead={selectedLead}
      />
    </div>
  );
};

export default DiscardedLeadsList;
